import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HTTP_STATUS_CODE } from '@core/common/http-status-code';
import { GlobalService } from '../services/global.service';
import { MESSAGE_TYPE } from '@app/shared/models/message.model';
import { AppConstant } from '@app/app.constant';
import { ErrorService } from '@core/services/error.service';

/**
 * Whether `url` is a call to Redoc's own API (`AppConstant.API_HOST`), the only host whose 404 means "this page's record
 * does not exist" and so opens /404. Another host's 404 — Reed's admin routes on an older Reed, a storage URL, OneMap,
 * data.gov.sg, a static asset — stays with the caller, which shows it in place.
 */
export function isApiHostRequest(url: string, apiHost: string = AppConstant.API_HOST): boolean {
    const host = (apiHost || '').replace(/\/+$/, '');
    return !!host && (url === host || url.startsWith(`${host}/`) || url.startsWith(`${host}?`));
}

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    constructor(
        private router: Router,
        private globalService: GlobalService
    ) {
    }

    intercept(
        request: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse | any) => {
                if (error instanceof HttpErrorResponse) {
                    if (AppConstant.APP_NOTIFICATION_DEBUG) {
                        this.globalService.message.next({
                            type: MESSAGE_TYPE.error,
                            title: error.status + ' ' + error.statusText,
                            message: ErrorService.parseMessage(error.error)
                        });
                    }
                    const statusCodeExclude = request.headers.getAll(AppConstant.HTTP_ERROR.HTTP_STATUS_CODE_EXCLUDE);
                    if (statusCodeExclude && statusCodeExclude.find(code => +code === error.status)) {
                        return throwError(error.error);
                    }
                    switch (error.status) {
                        // 401 is deliberately not handled here any more: TokenRefreshService owns
                        // it (refresh, then forced logout when the Id session is really over).
                        // Navigating to logout from here as well made two owners race.
                        case HTTP_STATUS_CODE.FORBIDDEN:
                            if (error.error.message === 'Invalid token') {
                                this.router.navigate(['/auth', 'login']);
                            }
                            break;
                        case HTTP_STATUS_CODE.NOT_FOUND:
                            if (isApiHostRequest(request.url)) {
                                this.router.navigate(['/404']);
                            }
                            break;
                        default: {

                        }
                    }
                    return throwError(error.error);
                }
                return throwError(error);
            })
        );
    }
}
